import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { SupplyControlIcons } from './SupplyControlIcons';
import NetworkBackground from './NetworkBackground';
import RefreshModal from './RefreshModal';
import SpotlightPurchaseModal from './SpotlightPurchaseModal';
import { useSocketUpdates, PriceUpdate, ListDiff } from '../hooks/useSocketUpdates';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, getApiHost, useSpaVerifying } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import { EVM_CHAIN_INFO, isEvmAddress, parseEvmChain, tokenPath } from '../utils/chains';
import { imageCandidates } from '../utils/ipfs';


interface RecentTokenData {
  mint: string;
  /** EVM chain id; absent for Solana */
  chain?: string;
  name: string;
  symbol: string;
  uri: string;
  uriMetadata?: any;
  topNetworkPercentage: number;
  riskLevel: string;
  createdAt: string;
  topHolderPercentage: number;
  holders: number;
  timestamp?: string;
  hasHolderAnalysis?: boolean;
  volume_30m_usd?: number;
  market_cap?: number;
  peakMultiplier?: number;
  currentMultiplier?: number;
  currentPrice?: number;
  priceAtWatchlistAdded?: number;
  isNew?: boolean;   // < 2h old
  gated?: boolean;   // requires Watcher+ tier
  /** Short risk label for items in the Flagged column. */
  flag?: 'liquidity rug' | 'curve buyout' | 'mintable' | 'freezable' | 'unlocked LP' | 'rugger owned' | 'creator' | 'cabal' | 'bundled' | 'concentrated' | 'flagged';
  /** Supply-control percentages — surfaced as inline icons next to the symbol. */
  lockedPercent?: number;
  stakedPercent?: number;
  multisigPercent?: number;
  multisigVaultCount?: number;
  multisigThreshold?: number;
  multisigMemberCount?: number;
  burnPercent?: number;
  /** Donate.gg charity-coin enrichment — set when listed at donate.gg/charity-coins. */
  charityName?: string;
  charityDonationSource?: string;
}

export interface TokenUriMetadataProperties {
  category: string;
}

export interface TokenUriMetadataExtensions {
  twitter: string;
  website: string;
  telegram: string;
}

export interface TokenUriMetadata {
  name: string;
  symbol: string;
  description: string;
  image: string;
  twitter: string;
  telegram: string;
  website: string;
  attributes: unknown[];
  properties: TokenUriMetadataProperties;
  extensions: TokenUriMetadataExtensions;
}

export type SearchResultItem = SearchResultToken | SearchResultWallet | SearchResultNetwork; 

export interface SearchResultNetwork {
  searchResultType: 'network';
  walletAddress: string;
  exchange?: string;
  topFundingSource: string;
  fundedAt?: Date;
  totalNetworkSize: number;
}

export interface SearchResultWallet {
  searchResultType: 'wallet';
  walletAddress: string;
  exchange?: string;
  topFundingSource: string;
  fundedAt?: Date;
}

export interface SearchResultToken {
  searchResultType: 'token';
  mint: string;
  name: string;
  symbol: string;
  uri?: string;
  uriMetadata?: TokenUriMetadata;
  networkPercentage: string;
  riskLevel?: SafetyLevel;
  createdAt?: Date;
  totalHolders?: number;
  topNetworkOwnership: number;
  hasHolderData: boolean;
}

export type SafetyLevel = 'OK' | 'RISKY' | 'DANGEROUS'

interface SpotlightData {
  mint: string;
  name: string;
  symbol: string;
  tier: 'regular' | 'sponsor';
  expiresAt: string;
  uri?: string;
  uriMetadata?: any;
  timestamp?: string;
  market_cap?: number;
  currentPrice?: number;
}

/**
 * A Solana mint/wallet address: base58, 32-44 chars. Worth detecting because the
 * average search query is 43.5 characters — people overwhelmingly PASTE an
 * address rather than type a name. A paste arrives complete in one event, so
 * debouncing it buys nothing and only adds latency to the commonest case.
 */
const LOOKS_LIKE_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

// Typed queries still need a debounce, but 600ms was far too long: the spinner
// only appeared AFTER it elapsed, so a p90 search showed nothing for ~1.7s
// (600ms wait + 1107ms request) and a p99 for ~7.5s. 64 users rage-clicked this
// input in 14 days, 292 clicks — the single biggest UX problem measured on the site.
const SEARCH_DEBOUNCE_MS = 250;

const MainPage: React.FC = () => {
  const { isAuthenticated, refreshUser, openLogin } = useAuth();
  const [bundles, setBundles] = useState<RecentTokenData[]>([]);
  // Flagged column: websocket-delivered inserts wait here and are released
  // to `bundles` one at a time by a staggered effect below. This prevents
  // 10 items all animating in simultaneously.
  const [pendingFlagged, setPendingFlagged] = useState<RecentTokenData[]>([]);
  const [watchlistTokens, setWatchlistTokens] = useState<RecentTokenData[]>([]);
  const [performers, setPerformers] = useState<RecentTokenData[]>([]);
  const [bundlesLoading, setBundlesLoading] = useState(true);
  const [watchlistLoading, setWatchlistLoading] = useState(true);
  const [performersLoading, setPerformersLoading] = useState(true);
  const [bundlesError, setBundlesError] = useState<string | null>(null);
  const [watchlistError, setWatchlistError] = useState<string | null>(null);
  const [performersError, setPerformersError] = useState<string | null>(null);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [spotlights, setSpotlights] = useState<SpotlightData[]>([]);
  const [spotlightModalOpen, setSpotlightModalOpen] = useState(false);
  const [spotlightPreselect, setSpotlightPreselect] = useState<{ mint: string; name: string; symbol: string } | null>(null);

  // Open spotlight modal from URL params (e.g. ?spotlight=open&mint=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('spotlight') === 'open') {
      const mint = params.get('mint');
      if (mint) {
        setSpotlightPreselect({ mint, name: '', symbol: '' });
      }
      setSpotlightModalOpen(true);
      trackEvent('spotlight_modal_opened', {
        source: 'token_page_cta',
        mint: params.get('mint') ?? null,
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchSpotlights = async () => {
    try {
      const response = await apiFetch(`${getApiHost()}/api/spotlight/active`);
      if (response.ok) {
        const data = await response.json();
        setSpotlights(data);
      }
    } catch {
      // Non-critical — don't block the page
    }
  };

  useEffect(() => {
    fetchBundles();
    fetchWatchlist();
    fetchPerformers();
    fetchSpotlights();
    // Refresh spotlights every 60s
    const interval = setInterval(fetchSpotlights, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Live updates via WebSocket
  const socketRooms = useMemo(() => ['watchlist' as const, 'performers' as const, 'bundles' as const, 'prices' as const], []);

  // ── Front-page list snapshot/diff handlers ─────────────────────────────
  // Snapshots replace the full list (sent on connect and every 30s scan cycle)
  const handleWatchlistSnapshot = useCallback((items: any[]) => {
    setWatchlistTokens(items);
    setWatchlistLoading(false);
  }, []);

  const handlePerformersSnapshot = useCallback((items: any[]) => {
    setPerformers(items);
    setPerformersLoading(false);
  }, []);

  // Diffs apply incremental add/remove changes
  const handleWatchlistDiff = useCallback((diff: ListDiff) => {
    setWatchlistTokens(prev => {
      // Remove tokens no longer on the list
      let updated = prev.filter(t => !diff.removed.includes(t.mint));
      // Add new tokens (prepend)
      for (const item of diff.added) {
        if (!updated.find(t => t.mint === item.mint)) {
          updated.unshift(item);
        }
      }
      return updated;
    });
  }, []);

  const handlePerformersDiff = useCallback((diff: ListDiff) => {
    setPerformers(prev => {
      let updated = prev.filter(t => !diff.removed.includes(t.mint));
      for (const item of diff.added) {
        if (!updated.find(t => t.mint === item.mint)) {
          updated.push(item);
        }
      }
      // Re-sort by multiplier descending
      updated.sort((a, b) => (b.currentMultiplier || 0) - (a.currentMultiplier || 0));
      return updated;
    });
  }, []);

  // Keep a ref of the current displayed list + pending queue so the
  // websocket handler can classify updates vs inserts without stale closures.
  const bundlesRef = useRef<RecentTokenData[]>([]);
  useEffect(() => { bundlesRef.current = bundles; }, [bundles]);
  const pendingFlaggedRef = useRef<RecentTokenData[]>([]);
  useEffect(() => { pendingFlaggedRef.current = pendingFlagged; }, [pendingFlagged]);

  const handleBundlesUpdate = useCallback((items: any[]) => {
    const displayedMints = new Set(bundlesRef.current.map(t => t.mint));
    const queuedMints = new Set(pendingFlaggedRef.current.map(t => t.mint));
    const toUpdate: any[] = [];
    const toInsert: any[] = [];
    for (const item of items) {
      if (displayedMints.has(item.mint)) toUpdate.push(item);
      else if (!queuedMints.has(item.mint)) toInsert.push(item);
      // If already queued, drop — the pending entry will land soon.
    }
    if (toUpdate.length > 0) {
      setBundles(prev => {
        const updated = [...prev];
        for (const item of toUpdate) {
          const idx = updated.findIndex(t => t.mint === item.mint);
          if (idx >= 0) updated[idx] = { ...updated[idx], ...item };
        }
        return updated;
      });
    }
    if (toInsert.length > 0) {
      setPendingFlagged(prev => [...prev, ...toInsert]);
    }
  }, []);

  // Staggered release: shift one pending item onto the displayed list every
  // 400ms so multiple incoming updates cascade in instead of piling up at
  // once. Each row's mount animation (see .flag-slide-in in index.css)
  // lasts 500ms, so releases overlap slightly for a continuous wave.
  useEffect(() => {
    if (pendingFlagged.length === 0) return;
    const STAGGER_MS = 400;
    const timer = setTimeout(() => {
      setPendingFlagged(prev => {
        if (prev.length === 0) return prev;
        const [next, ...rest] = prev;
        setBundles(curr => {
          if (curr.some(t => t.mint === next.mint)) return curr;
          return [next, ...curr].slice(0, 10);
        });
        return rest;
      });
    }, STAGGER_MS);
    return () => clearTimeout(timer);
  }, [pendingFlagged]);

  const handlePriceUpdate = useCallback((updates: PriceUpdate[]) => {
    // Update watchlist tokens
    setWatchlistTokens(prev => {
      const updated = [...prev];
      let changed = false;
      for (const update of updates) {
        const idx = updated.findIndex(t => t.mint === update.mint);
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            market_cap: update.marketCap,
            currentPrice: update.priceSol,
          };
          changed = true;
        }
      }
      return changed ? updated : prev;
    });

    // Update performers with recalculated multiplier
    setPerformers(prev => {
      const updated = [...prev];
      let changed = false;
      for (const update of updates) {
        const idx = updated.findIndex(t => t.mint === update.mint);
        if (idx >= 0) {
          const token = updated[idx];
          const priceAtAdded = token.priceAtWatchlistAdded;
          // priceAtWatchlistAdded is in SOL, so use priceSol for multiplier
          const newMultiplier = priceAtAdded && priceAtAdded > 0 && update.priceSol > 0
            ? update.priceSol / priceAtAdded
            : token.currentMultiplier;
          updated[idx] = {
            ...token,
            market_cap: update.marketCap,
            currentPrice: update.priceSol,  // keep in SOL for consistency with perf eval
            currentMultiplier: newMultiplier,
          };
          changed = true;
        }
      }
      // Don't re-sort here - let PerformersColumn handle it
      return changed ? updated : prev;
    });

    // Update spotlights with live prices
    setSpotlights(prev => {
      const updated = [...prev];
      let changed = false;
      for (const update of updates) {
        const idx = updated.findIndex(s => s.mint === update.mint);
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            market_cap: update.marketCap,
            currentPrice: update.priceSol,
          };
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, []);

  useSocketUpdates({
    rooms: socketRooms,
    onWatchlistSnapshot: handleWatchlistSnapshot,
    onWatchlistDiff: handleWatchlistDiff,
    onPerformersSnapshot: handlePerformersSnapshot,
    onPerformersDiff: handlePerformersDiff,
    onBundlesUpdate: handleBundlesUpdate,
    onPriceUpdate: handlePriceUpdate,
  });

  const fetchBundles = async () => {
    try {
      setBundlesLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/flagged-tokens`);
      if (!response.ok) {
        throw new Error('Failed to fetch flagged tokens');
      }
      const data = await response.json();
      setBundles(data);
    } catch (err) {
      setBundlesError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setBundlesLoading(false);
    }
  };

  const fetchWatchlist = async () => {
    try {
      setWatchlistLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/watchlist`);
      if (!response.ok) {
        throw new Error('Failed to fetch watchlist');
      }
      const data = await response.json();
      setWatchlistTokens(data);
    } catch (err) {
      setWatchlistError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setWatchlistLoading(false);
    }
  };

  const fetchPerformers = async () => {
    try {
      setPerformersLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/watchlist-performers`);
      if (!response.ok) {
        throw new Error('Failed to fetch performers');
      }
      const data = await response.json();
      // Map API response to the field names the render + WS handler use.
      // /api/watchlist-performers returns live values as `liveCurrentMultiplier`,
      // `livePriceSol`, `liveMarketCapUsd` (computed against token_prices in
      // the aggregation). The deprecated nested `performanceEvaluation.current*`
      // fields are no longer populated, so prefer the live ones first —
      // otherwise initial render shows everything at 1x until WS updates land.
      const mapped = data.map((item: any) => ({
        ...item,
        priceAtWatchlistAdded: item.priceAtWatchlistAdded || item.performanceEvaluation?.priceAtWatchlistAdded || item.price,
        currentMultiplier: item.liveCurrentMultiplier ?? item.performanceEvaluation?.currentMultiplier ?? item.currentMultiplier,
        currentPrice:      item.livePriceSol           ?? item.performanceEvaluation?.currentPrice      ?? item.currentPrice,
        market_cap:        item.liveMarketCapUsd       ?? item.market_cap,
        peakMultiplier:    item.performanceEvaluation?.peakMultiplier || item.peakMultiplier,
      }));
      setPerformers(mapped);
    } catch (err) {
      setPerformersError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setPerformersLoading(false);
    }
  };

  const requestHolderAnalysis = async (mint: string) => {
    // Require login
    if (!isAuthenticated) {
      openLogin('home');
      return;
    }

    try {
      const token = localStorage.getItem('deepnets_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await apiFetch(`${getApiHost()}/api/request-token-safety-analysis`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mint }),
      });

      if (response.status === 401) {
        // Token expired or invalid — clear stale state and prompt login
        localStorage.removeItem('deepnets_token');
        refreshUser();
        openLogin('home');
        return;
      }

      if (response.status === 402) {
        const data = await response.json();
        alert(`Insufficient credits. You need 1 credit but have ${data.available ?? 0}. Visit your profile to get more credits.`);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to request analysis');
      }

      const data = await response.json();
      setCurrentRequestId(data.requestId);
      setRefreshModalOpen(true);
      // Refresh user data to update credit balance in header
      refreshUser();
    } catch (error) {
      console.error('Error requesting analysis:', error);
      alert('Failed to request analysis. Please try again.');
    }
  };

  const handleRefreshSuccess = () => {
    // Refresh the page to show updated analysis
    window.location.reload();
  };

  const handleRefreshError = (errorMessage: string) => {
    // Show error message
    alert(`Analysis failed: ${errorMessage}`);
  };

  const handleModalClose = () => {
    setRefreshModalOpen(false);
    setCurrentRequestId(null);
  };

  return (
    <div className="min-h-screen bg-crypto-dark text-white relative">
      <NetworkBackground />
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-4 relative z-10">
        <HeroSearch />
        <TokenColumnsSection
          bundles={bundles}
          watchlistTokens={watchlistTokens}
          performers={performers}
          spotlights={spotlights}
          bundlesLoading={bundlesLoading}
          watchlistLoading={watchlistLoading}
          performersLoading={performersLoading}
          bundlesError={bundlesError}
          watchlistError={watchlistError}
          performersError={performersError}
          onRequestAnalysis={requestHolderAnalysis}
        />
        <StatsOverview />
      </main>
      <Footer />

      <RefreshModal
        isOpen={refreshModalOpen}
        requestId={currentRequestId}
        onClose={handleModalClose}
        onSuccess={handleRefreshSuccess}
        onError={handleRefreshError}
      />

      <SpotlightPurchaseModal
        isOpen={spotlightModalOpen}
        onClose={() => { setSpotlightModalOpen(false); setSpotlightPreselect(null); }}
        preselectedMint={spotlightPreselect?.mint}
        preselectedName={spotlightPreselect?.name}
        preselectedSymbol={spotlightPreselect?.symbol}
      />
    </div>
  );
};

const HeroSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [stats, setStats] = useState<{ tokensAnalyzed: number } | null>(null);
  const navigate = useNavigate();

  // Fetch stats for the hero section
  useEffect(() => {
    apiFetch(`${getApiHost()}/api/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStats(data); })
      .catch(() => {});
  }, []);

  // Shared by the click path and the paste-auto-navigate path. Takes the result
  // list explicitly because the auto path fires before setSearchResults has
  // committed, so reading state here would give a stale (empty) list.
  const goToResult = useCallback((result: SearchResultItem, list: SearchResultItem[], auto: boolean) => {
    trackEvent('search_result_clicked', {
      resultType: result.searchResultType,
      hasHolderData: result.searchResultType === 'token' ? !!result.hasHolderData : null,
      position: list.findIndex(r => r === result),
      resultCount: list.length,
      auto,
    });
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    if (result.searchResultType === 'token') {
      // Always the public token page, analysed or not. The old split sent
      // unanalysed mints to /holder-analysis because /token/:mint was said to
      // 404 without safety data — no longer true: a valid mint returns a `hint`
      // and the page handles it, auto-running a scan for signed-in users and
      // showing a clean "sign in to analyze it" otherwise.
      navigate(`/token/${result.mint}`);
    } else if (result.searchResultType === 'network') {
      navigate(`/network/${result.walletAddress}`);
    } else if (result.searchResultType === 'wallet') {
      navigate(`/wallet/${result.walletAddress}`);
    }
  }, [navigate]);

  const handleSearch = useCallback(async (query: string, autoNavigate = false) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    const t0 = Date.now();
    try {
      const response = await apiFetch(`${getApiHost()}/api/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
        setShowResults(true);
        // Track BEFORE any early return. The auto-navigate branch below returns,
        // and having this after it meant every auto-navigated search went
        // unrecorded — which is the dominant path, so search_performed fell from
        // ~890/day to ~146 and made the search_failed RATE look like it was
        // climbing when only the denominator had gone.
        trackEvent('search_performed', {
          queryLength: query.trim().length,
          resultCount: Array.isArray(results) ? results.length : 0,
          ms: Date.now() - t0,
          // Lets the two paths be compared rather than conflated.
          autoNavigated: !!(autoNavigate && Array.isArray(results) && results.length === 1),
        });
        // A pasted address that resolves to exactly one thing is unambiguous —
        // go straight there rather than rendering a one-row dropdown and asking
        // for a second click. 97% of searches are address pastes, and of those
        // 82.5% return exactly 1 result and none return more, so this is the
        // dominant path. Deliberately NOT done for name/symbol queries: those
        // must keep showing ranked candidates, because tickers get reused by
        // impostors and picking one for the user would hide that.
        if (autoNavigate && Array.isArray(results) && results.length === 1) {
          goToResult(results[0], results, true);
          return;
        }
      } else {
        trackEvent('search_failed', { status: response.status, ms: Date.now() - t0 });
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      trackEvent('search_failed', { status: 0, ms: Date.now() - t0 });
    } finally {
      setIsSearching(false);
    }
  }, [goToResult]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    // Show the spinner the moment there is something to search for, rather than
    // when the request finally starts. This is the actual fix for the rage-
    // clicking: the control now responds to the first keystroke or paste.
    setIsSearching(true);

    // An EVM (0x) token address: the token page resolves which chain it trades
    // on (auto-picks a single chain, asks when there are several).
    if (isEvmAddress(q)) {
      trackEvent('search_result_clicked', { resultType: 'evm_address', hasHolderData: null, position: -1, resultCount: 0, auto: true });
      setSearchQuery('');
      navigate(`/token/${q.toLowerCase()}`);
      return;
    }

    // A complete address arrives in one paste event — settling time buys nothing
    // and only delays the commonest case. Go straight out.
    if (LOOKS_LIKE_ADDRESS.test(q)) {
      handleSearch(searchQuery, true);
      return;
    }

    const debounceTimer = setTimeout(() => {
      handleSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, handleSearch, navigate]);

  const handleResultClick = (result: SearchResultItem) => goToResult(result, searchResults, false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    } else if (e.key === 'Escape') {
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.search-container')) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="text-center mb-6">
      <h1 className="text-4xl sm:text-5xl font-semibold mb-2 text-white tracking-tight">
        Deep Insight, No Digging
      </h1>
      <p className="text-lg text-gray-400 mb-2 max-w-2xl mx-auto font-normal">
        Deep funding-network mapping, holder research, and safety analysis for Solana tokens.
      </p>

      {/* Value Proposition Bullets */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-3 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-crypto-accent"></span>
          <span>Detects coordinated wallet networks</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-crypto-red"></span>
          <span>Identifies liquidity traps</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-crypto-yellow"></span>
          <span>
            {stats?.tokensAnalyzed
              ? `${stats.tokensAnalyzed.toLocaleString()}+ tokens analyzed daily`
              : 'Thousands of tokens analyzed daily'}
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto relative search-container">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Paste token mint address or search by name/symbol..."
            className="w-full bg-crypto-gray border border-gray-700 rounded-xl px-6 py-4 text-lg focus:outline-none focus:border-crypto-accent transition-colors pr-16"
            autoComplete="off"
          />
          {/* This button previously had no handler at all: clicking it did
              nothing, and it stayed disabled for the whole request. 25 of the
              homepage rage-clicks landed on it. It now commits the search —
              opening the top result if one is ready, otherwise running the
              query immediately, which also lets an impatient user skip the
              debounce rather than clicking repeatedly. */}
          <button
            type="button"
            onClick={() => {
              if (searchResults.length > 0) { handleResultClick(searchResults[0]); return; }
              if (searchQuery.trim().length >= 2) handleSearch(searchQuery);
            }}
            aria-label="Search"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50"
          >
            {isSearching ? (
              <i className="fas fa-spinner fa-spin"></i>
            ) : (
              <i className="fas fa-search"></i>
            )}
          </button>
        </div>

        {showResults && searchResults.length > 0 && (
          <div className="absolute top-full mt-2 w-full bg-crypto-gray border border-gray-700 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
            {searchResults.map((result) => {
              if (result.searchResultType === 'token') {
                return (
                  <div
                    key={result.mint}
                    className="flex items-center space-x-4 p-4 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                    onClick={() => handleResultClick(result)}
                  >
                    <TokenImage uri={result.uri || ''} uriMetadata={result.uriMetadata} symbol={result.symbol} name={result.name} />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center space-x-2 mb-1 min-w-0">
                        <span className="font-bold shrink-0">{result.symbol}</span>
                        <span className="text-gray-300 text-sm truncate">{result.name}</span>
                      </div>
                      <div className="text-gray-400 text-sm">
                        {result.hasHolderData
                          ? `${result.totalHolders} holders • ${result.networkPercentage}% network owned`
                          : 'No holder analysis available'
                        }
                        {/* Only show "Created …" when we actually know the
                            creation time — i.e., the token exists in our
                            tokens collection. For pure Birdeye-search
                            results the backend now sends a null
                            createdAt rather than synthesizing "now". */}
                        {result.createdAt && (
                          <>
                            {' • '}
                            Created {formatTimeAgo(typeof result.createdAt === 'string' ? result.createdAt : result.createdAt.toISOString())}
                          </>
                        )}
                      </div>
                      <div className="text-gray-500 text-xs font-mono truncate">
                        {result.mint}
                      </div>
                    </div>
                    {result.hasHolderData && result.riskLevel && (
                      <div className={`text-sm font-medium px-2 py-1 rounded shrink-0 whitespace-nowrap ${
                        result.riskLevel === 'DANGEROUS' ? 'bg-crypto-red text-white' :
                        result.riskLevel === 'RISKY' ? 'bg-crypto-red text-white' :
                        'bg-crypto-accent text-crypto-dark'
                      }`}>
                        {result.riskLevel}
                      </div>
                    )}
                  </div>
                );
              } else if (result.searchResultType === 'network') {
                return (
                  <div
                    key={result.walletAddress}
                    className="flex items-center space-x-4 p-4 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                    onClick={() => handleResultClick(result)}
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center">
                      <i className="fas fa-wallet text-gray-400"></i>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-bold">Funding Network Root Wallet</span>
                        <span className="text-gray-300 text-sm">({result.totalNetworkSize} wallets)</span>
                      </div>
                      <div className="text-gray-500 text-xs font-mono truncate">
                        {result.walletAddress}
                      </div>
                    </div>
                  </div>
                );
              } else if (result.searchResultType === 'wallet') {
                return (
                  <div
                    key={result.walletAddress}
                    className="flex items-center space-x-4 p-4 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                    onClick={() => handleResultClick(result)}
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center">
                      <i className="fas fa-wallet text-gray-400"></i>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-bold">Wallet</span>
                        {result.exchange && <span className="text-gray-300 text-sm">({result.exchange})</span>}
                      </div>
                      <div className="text-gray-400 text-sm">
                        Top funding source: {result.topFundingSource}
                      </div>
                      <div className="text-gray-500 text-xs font-mono truncate">
                        {result.walletAddress}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}

        {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
          <div className="absolute top-full mt-2 w-full bg-crypto-gray border border-gray-700 rounded-xl shadow-lg z-50 p-4 text-center text-gray-400">
            {/* A query that looks like a Solana address is almost certainly a
                real mint we just haven't researched yet — show an inviting
                "research it" state instead of an error-looking "no results".
                Sends to /token/:mint, matching handleResultClick: it no longer
                404s on an unresearched mint (a valid one returns a `hint` and
                the page auto-runs a scan for signed-in users), and it shows a
                clean "sign in to analyze it" for everyone else. */}
            {LOOKS_LIKE_ADDRESS.test(searchQuery.trim()) ? (
              <>
                <div className="mb-1 text-gray-200">We haven't analyzed this token yet.</div>
                <div className="mb-3 text-gray-500 text-xs font-mono break-all">{searchQuery.trim()}</div>
                <button
                  onClick={() => {
                    trackEvent('search_result_clicked', { resultType: 'unresearched_mint', hasHolderData: false, position: -1, resultCount: 0 });
                    navigate(`/token/${searchQuery.trim()}`);
                  }}
                  className="inline-block bg-crypto-accent text-crypto-dark font-medium px-4 py-2 rounded-lg hover:bg-crypto-accent/80 text-sm"
                >
                  Research this token
                </button>
              </>
            ) : (
              <div>No tokens found matching "{searchQuery}"</div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const TokenColumnsSection: React.FC<{
  bundles: RecentTokenData[];
  watchlistTokens: RecentTokenData[];
  performers: RecentTokenData[];
  spotlights: SpotlightData[];
  bundlesLoading: boolean;
  watchlistLoading: boolean;
  performersLoading: boolean;
  bundlesError: string | null;
  watchlistError: string | null;
  performersError: string | null;
  onRequestAnalysis: (mint: string) => Promise<void>;
}> = ({ bundles, watchlistTokens, performers, spotlights, bundlesLoading, watchlistLoading, performersLoading, bundlesError, watchlistError, performersError, onRequestAnalysis }) => {
  return (
    <section className="mb-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Performers Column */}
        <PerformersColumn performers={performers} spotlights={spotlights} loading={performersLoading} error={performersError} onRequestAnalysis={onRequestAnalysis} />

        {/* Watchlist Column */}
        <WatchlistColumn tokens={watchlistTokens} loading={watchlistLoading} error={watchlistError} onRequestAnalysis={onRequestAnalysis} />

        {/* Bundled Tokens Column */}
        <BundledTokensColumn bundles={bundles} loading={bundlesLoading} error={bundlesError} onRequestAnalysis={onRequestAnalysis} />
      </div>
    </section>
  );
};

const formatMarketCap = (marketCap: number | undefined): string => {
  if (!marketCap || marketCap <= 0) return '';
  if (marketCap < 1000) return '';
  if (marketCap < 1_000_000) return `${(marketCap / 1000).toFixed(1)}K`;
  if (marketCap < 1_000_000_000) return `${(marketCap / 1_000_000).toFixed(1)}M`;
  return `${(marketCap / 1_000_000_000).toFixed(1)}B`;
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays}d ago`;
  } else if (diffHours > 0) {
    return `${diffHours}h ago`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes}m ago`;
  } else {
    return 'now';
  }
};

const BundledTokensColumn: React.FC<{
  bundles: RecentTokenData[];
  loading: boolean;
  error: string | null;
  onRequestAnalysis: (mint: string) => Promise<void>;
}> = ({ bundles, loading, error, onRequestAnalysis }) => {
  const verifying = useSpaVerifying();
  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold flex items-center">
            <i className="fas fa-exclamation-triangle text-crypto-red mr-3"></i>
            Flagged
          </h2>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <i className="fas fa-spinner fa-spin"></i>
            <span>{verifying ? 'Verifying your browser…' : 'Loading...'}</span>
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-crypto-gray border border-gray-700 rounded-xl py-2 px-3 animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gray-600 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-600 rounded mb-2 w-1/2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold flex items-center">
            <i className="fas fa-exclamation-triangle text-crypto-red mr-3"></i>
            Flagged
          </h2>
        </div>
        <div className="text-center py-12 bg-crypto-gray border border-gray-700 rounded-xl">
          <div className="text-red-400 mb-4">
            <i className="fas fa-exclamation-triangle text-4xl"></i>
          </div>
          <p className="text-gray-400">Failed to load bundles: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-2 rounded-lg font-medium transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center">
          <i className="fas fa-exclamation-triangle text-crypto-red mr-3"></i>
          Flagged
        </h2>
      </div>

      <div className="flex flex-col gap-2">
        {bundles.map((bundle) => {
          const flag = bundle.flag || 'flagged';
          return (
            <div key={bundle.mint} className="flag-slide-in">
            <Link
              to={tokenPath(bundle.mint, bundle.chain)}
              className="block bg-crypto-gray border border-gray-700 rounded-xl py-2 px-3 hover:border-crypto-red/40 hover:shadow-lg hover:shadow-crypto-red/5 transition-all duration-200 cursor-pointer"
            >
              <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
                <TokenImage uri={bundle.uri} uriMetadata={bundle.uriMetadata} symbol={bundle.symbol} name={bundle.name} />
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 mb-1">
                    <h3 className="font-semibold text-[15px] text-white whitespace-nowrap inline-flex items-baseline">
                      {bundle.symbol}
                      <ChainBadge chain={bundle.chain} />
                      <SupplyControlIcons
                        locked={bundle.lockedPercent}
                        staked={bundle.stakedPercent}
                        multisig={bundle.multisigPercent}
                        burned={bundle.burnPercent}
                        multisigVaultCount={bundle.multisigVaultCount}
                        multisigThreshold={bundle.multisigThreshold}
                        multisigMemberCount={bundle.multisigMemberCount}
                      />
                    </h3>
                    <span className="text-gray-400 text-[13px] truncate">{bundle.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[12px] whitespace-nowrap">
                    <span className="text-gray-500 font-mono">{bundle.mint.slice(0, 4)}…{bundle.mint.slice(-4)}</span>
                    <span className="text-gray-600">·</span>
                    <span className="text-gray-500">{formatTimeAgo(bundle.timestamp || bundle.createdAt)}</span>
                  </div>
                </div>
                <span className="text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap text-crypto-red bg-crypto-red/10">
                  {flag}
                </span>
              </div>
            </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WatchlistColumn: React.FC<{
  tokens: RecentTokenData[];
  loading: boolean;
  error: string | null;
  onRequestAnalysis: (mint: string) => Promise<void>;
}> = ({ tokens, loading, error, onRequestAnalysis }) => {
  const verifying = useSpaVerifying();
  const TOTAL_VISIBLE = 10;
  // Chain filter pills appear only once the list actually mixes chains.
  const [chainFilter, setChainFilter] = useState<string>('all');
  const chainsPresent = Array.from(new Set(tokens.map(t => t.chain ?? 'solana')));
  const filtered = chainFilter === 'all' ? tokens : tokens.filter(t => (t.chain ?? 'solana') === chainFilter);
  const remainingTokens = filtered.slice(0, TOTAL_VISIBLE);
  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold flex items-center">
            <i className="fas fa-list text-crypto-accent mr-3"></i>
            New Watchlist
          </h2>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <i className="fas fa-spinner fa-spin"></i>
            <span>{verifying ? 'Verifying your browser…' : 'Loading...'}</span>
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-crypto-gray border border-gray-700 rounded-xl py-2 px-3 animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gray-600 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-600 rounded mb-2 w-1/2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold flex items-center">
            <i className="fas fa-list text-crypto-accent mr-3"></i>
            New Watchlist
          </h2>
        </div>
        <div className="text-center py-12 bg-crypto-gray border border-gray-700 rounded-xl">
          <div className="text-red-400 mb-4">
            <i className="fas fa-exclamation-triangle text-4xl"></i>
          </div>
          <p className="text-gray-400">Failed to load watchlist: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-2 rounded-lg font-medium transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center">
          <i className="fas fa-list text-crypto-accent mr-3"></i>
          New Watchlist
        </h2>
        {chainsPresent.length > 1 && (
          <div className="flex items-center gap-1 text-[11px]" role="group" aria-label="Filter by chain">
            {['all', ...chainsPresent].map(c => (
              <button key={c} onClick={() => setChainFilter(c)} aria-pressed={chainFilter === c}
                className={`px-2 py-0.5 rounded border transition-colors ${chainFilter === c ? 'border-crypto-accent text-crypto-accent' : 'border-gray-700 text-gray-400 hover:text-white'}`}>
                {c === 'all' ? 'All' : c === 'solana' ? 'SOL' : (EVM_CHAIN_INFO[parseEvmChain(c) ?? 'base']?.short ?? c)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {remainingTokens.map((token) => {
          const addedAgo = token.timestamp ? formatTimeAgo(token.timestamp) : 'recently';
          const mcapFormatted = formatMarketCap(token.market_cap);

          return (
            <Link
              key={token.mint}
              to={tokenPath(token.mint, token.chain)}
              onClick={() => { try { navigator.sendBeacon(`${getApiHost()}/api/spotlight/click`, new Blob([JSON.stringify({ mint: token.mint, spotlighted: false, tier: null })], { type: 'application/json' })); } catch (_) {} }}
              className="block bg-crypto-gray border border-gray-700 rounded-xl py-2 px-3 hover:border-crypto-accent/40 hover:shadow-lg hover:shadow-crypto-accent/5 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <TokenImage uri={token.uri || ''} uriMetadata={token.uriMetadata} symbol={token.symbol} name={token.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <h3 className="font-semibold text-[15px] text-white inline-flex items-baseline">
                        {token.symbol}
                        <ChainBadge chain={token.chain} />
                        {token.charityName && (
                          <i
                            className="fas fa-hand-holding-heart text-pink-400 ml-1.5 text-[12px]"
                            title={`Charity coin — fees routed via ${token.charityDonationSource ?? 'donate.gg'} (listed on donate.gg)`}
                          />
                        )}
                        <SupplyControlIcons
                          locked={token.lockedPercent}
                          staked={token.stakedPercent}
                          multisig={token.multisigPercent}
                          burned={token.burnPercent}
                          multisigVaultCount={token.multisigVaultCount}
                          multisigThreshold={token.multisigThreshold}
                          multisigMemberCount={token.multisigMemberCount}
                        />
                      </h3>
                      <span className="text-gray-400 text-[13px] truncate">{token.name}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-gray-500">Added {addedAgo}</span>
                    {mcapFormatted && (
                      <span className="text-gray-500">${mcapFormatted}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const PerformersColumn: React.FC<{
  performers: RecentTokenData[];
  spotlights?: SpotlightData[];
  loading: boolean;
  error: string | null;
  onRequestAnalysis: (mint: string) => Promise<void>;
}> = ({ performers, spotlights = [], loading, error, onRequestAnalysis }) => {
  const verifying = useSpaVerifying();
  const [isHovered, setIsHovered] = useState(false);
  const [displayOrder, setDisplayOrder] = useState<string[]>([]);

  // Update display order when not hovered
  useEffect(() => {
    if (!isHovered && performers.length > 0) {
      const sorted = [...performers]
        .sort((a, b) => (b.currentMultiplier || 0) - (a.currentMultiplier || 0))
        .map(t => t.mint);
      setDisplayOrder(sorted);
    }
  }, [performers, isHovered]);

  // Initialize display order on first load
  useEffect(() => {
    if (displayOrder.length === 0 && performers.length > 0) {
      const sorted = [...performers]
        .sort((a, b) => (b.currentMultiplier || 0) - (a.currentMultiplier || 0))
        .map(t => t.mint);
      setDisplayOrder(sorted);
    }
  }, [performers, displayOrder.length]);

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold flex items-center">
            <i className="fas fa-chart-line text-green-400 mr-3"></i>
            Performers
          </h2>
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <i className="fas fa-spinner fa-spin"></i>
            <span>{verifying ? 'Verifying your browser…' : 'Loading...'}</span>
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-crypto-gray border border-gray-700 rounded-xl py-2 px-3 animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gray-600 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-600 rounded mb-2 w-1/2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold flex items-center">
            <i className="fas fa-chart-line text-green-400 mr-3"></i>
            Performers
          </h2>
        </div>
        <div className="text-center py-12 bg-crypto-gray border border-gray-700 rounded-xl">
          <div className="text-red-400 mb-4">
            <i className="fas fa-exclamation-triangle text-4xl"></i>
          </div>
          <p className="text-gray-400">Failed to load performers: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-2 rounded-lg font-medium transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Build a lookup map for performers
  const performerMap = new Map(performers.map(p => [p.mint, p]));

  // Spotlighted tokens render first; exclude them from the regular performers list to avoid duplicates.
  const spotlightMints = new Set(spotlights.map(s => s.mint));
  const spotlightedTokens = spotlights.map(s => {
    const p = performerMap.get(s.mint);
    const base: RecentTokenData = p || {
      mint: s.mint,
      name: s.name,
      symbol: s.symbol,
      uri: s.uri || '',
      uriMetadata: s.uriMetadata || undefined,
      timestamp: s.timestamp,
      market_cap: s.market_cap || 0,
      currentPrice: s.currentPrice || 0,
      topNetworkPercentage: 0,
      riskLevel: '',
      createdAt: '',
      topHolderPercentage: 0,
      holders: 0,
    };
    return { ...base, spotlightTier: s.tier as 'regular' | 'sponsor' };
  });

  // Get tokens in display order, filtering out any that no longer exist or are spotlighted
  const orderedPerformers = displayOrder
    .map(mint => performerMap.get(mint))
    .filter((t): t is RecentTokenData => t !== undefined && !spotlightMints.has(t.mint));

  // Add any new performers not in displayOrder (and not spotlighted)
  const displayOrderSet = new Set(displayOrder);
  const newPerformers = performers.filter(p => !displayOrderSet.has(p.mint) && !spotlightMints.has(p.mint));
  // Cap the column at 10 total rows (spotlights count toward the total).
  const TOTAL_VISIBLE = 10;
  const allOrderedPerformers = [...orderedPerformers, ...newPerformers]
    .slice(0, Math.max(0, TOTAL_VISIBLE - spotlightedTokens.length));

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center">
          <i className="fas fa-chart-line text-green-400 mr-3"></i>
          Performers
        </h2>
      </div>

      <div className="space-y-2">
        {/* Spotlighted tokens at the top */}
        {spotlightedTokens.map((token) => {
          const addedAgo = token.timestamp ? formatTimeAgo(token.timestamp) : 'recently';
          const mcapFormatted = formatMarketCap(token.market_cap);
          const isSponsor = token.spotlightTier === 'sponsor';

          return (
            <Link
              key={token.mint}
              to={`/token/${token.mint}`}
              onClick={() => { try { navigator.sendBeacon(`${getApiHost()}/api/spotlight/click`, new Blob([JSON.stringify({ mint: token.mint, spotlighted: true, tier: token.spotlightTier })], { type: 'application/json' })); } catch (_) {} }}
              className={`block rounded-xl py-2 px-3 transition-all duration-200 cursor-pointer ${
                isSponsor
                  ? 'bg-gradient-to-br from-purple-900/40 via-crypto-gray to-crypto-gray border border-purple-500/60 shadow-[0_4px_24px_-4px_rgba(168,85,247,0.35)] hover:border-purple-400/80 hover:shadow-[0_6px_32px_-4px_rgba(168,85,247,0.5)]'
                  : 'bg-gradient-to-br from-crypto-yellow/15 via-crypto-gray to-crypto-gray border border-crypto-yellow/50 shadow-[0_4px_24px_-4px_rgba(255,165,2,0.25)] hover:border-crypto-yellow/70 hover:shadow-[0_6px_32px_-4px_rgba(255,165,2,0.4)]'
              }`}
            >
              <div className="flex items-center gap-3">
                <TokenImage uri={token.uri || ''} uriMetadata={token.uriMetadata} symbol={token.symbol} name={token.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <h3 className="font-semibold text-[15px] text-white inline-flex items-baseline">
                        {token.symbol}
                        <ChainBadge chain={token.chain} />
                        {token.charityName && (
                          <i
                            className="fas fa-hand-holding-heart text-pink-400 ml-1.5 text-[12px]"
                            title={`Charity coin — fees routed via ${token.charityDonationSource ?? 'donate.gg'} (listed on donate.gg)`}
                          />
                        )}
                        <SupplyControlIcons
                          locked={token.lockedPercent}
                          staked={token.stakedPercent}
                          multisig={token.multisigPercent}
                          burned={token.burnPercent}
                          multisigVaultCount={token.multisigVaultCount}
                          multisigThreshold={token.multisigThreshold}
                          multisigMemberCount={token.multisigMemberCount}
                        />
                      </h3>
                      <span className="text-gray-400 text-[13px] truncate">{token.name}</span>
                    </div>
                    {isSponsor ? (
                      <span className="text-[10px] font-semibold text-purple-300 bg-purple-500/20 px-2 py-0.5 rounded-full flex items-center gap-1 ml-2 whitespace-nowrap">
                        <i className="fas fa-crown text-[8px]"></i>
                        SPONSOR
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold text-crypto-yellow bg-crypto-yellow/10 px-2 py-0.5 rounded-full flex items-center gap-1 ml-2 whitespace-nowrap">
                        <i className="fas fa-star text-[8px]"></i>
                        SPOTLIGHT
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-gray-500">Added {addedAgo}</span>
                    {mcapFormatted && (
                      <span className="text-gray-500">${mcapFormatted}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}

        {/* Regular performers */}
        {allOrderedPerformers.map((token) => {
          const multiplier = token.currentMultiplier || 1;
          const addedAgo = token.timestamp ? formatTimeAgo(token.timestamp) : 'recently';
          const mcapFormatted = formatMarketCap(token.market_cap);

          return (
            <Link
              key={token.mint}
              to={tokenPath(token.mint, token.chain)}
              className="block bg-crypto-gray border border-gray-700 rounded-xl py-2 px-3 hover:border-green-400/40 hover:shadow-lg hover:shadow-green-400/5 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <TokenImage uri={token.uri || ''} uriMetadata={token.uriMetadata} symbol={token.symbol} name={token.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <h3 className="font-semibold text-[15px] text-white inline-flex items-baseline">
                        {token.symbol}
                        <ChainBadge chain={token.chain} />
                        {token.charityName && (
                          <i
                            className="fas fa-hand-holding-heart text-pink-400 ml-1.5 text-[12px]"
                            title={`Charity coin — fees routed via ${token.charityDonationSource ?? 'donate.gg'} (listed on donate.gg)`}
                          />
                        )}
                        <SupplyControlIcons
                          locked={token.lockedPercent}
                          staked={token.stakedPercent}
                          multisig={token.multisigPercent}
                          burned={token.burnPercent}
                          multisigVaultCount={token.multisigVaultCount}
                          multisigThreshold={token.multisigThreshold}
                          multisigMemberCount={token.multisigMemberCount}
                        />
                      </h3>
                      <span className="text-gray-400 text-[13px] truncate">{token.name}</span>
                    </div>
                    <span className="text-green-400 font-semibold text-sm ml-2">{multiplier.toFixed(1)}x</span>
                  </div>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-gray-500">Added {addedAgo}</span>
                    {mcapFormatted && (
                      <span className="text-gray-500">${mcapFormatted}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/** Small chain tag for EVM rows; Solana rows render nothing. */
const ChainBadge: React.FC<{ chain?: string }> = ({ chain }) => {
  const c = parseEvmChain(chain);
  if (!c) return null;
  const info = EVM_CHAIN_INFO[c];
  return <span className={`ml-1.5 self-center text-[9px] font-semibold border px-1 py-px rounded ${info.badgeClass}`} title={`${info.name} token`}>{info.short}</span>;
};

const TokenImage: React.FC<{ uri: string; uriMetadata?: any; symbol: string; name: string }> = ({ uri, uriMetadata, symbol, name }) => {
  const [rawImage, setImageSrc] = useState<string | null>(null);
  const [candidateIdx, setCandidateIdx] = useState(0);
  const candidates = React.useMemo(() => imageCandidates(rawImage), [rawImage]);
  const imageSrc = candidates[candidateIdx] ?? null;
  const [loading, setLoading] = useState(true);
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number } | null>(null);
  const imgRef = React.useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Use uriMetadata if available, otherwise try to fetch from URI as fallback
    setCandidateIdx(0);
    if (uriMetadata) {
      setImageSrc(uriMetadata.image || null);
      setLoading(false);
    } else {
      // Fallback to fetching from URI if uriMetadata is not available
      const fetchMetadata = async () => {
        try {
          // Metadata JSON is usually on IPFS too; use the first working gateway.
          const response = await fetch(imageCandidates(uri)[0] ?? uri);
          if (response.ok) {
            const metadata = await response.json();
            setImageSrc(metadata.image || null);
          }
        } catch (error) {
          console.warn(`Failed to fetch metadata for ${name}:`, error);
        } finally {
          setLoading(false);
        }
      };

      fetchMetadata();
    }
  }, [uriMetadata, uri, name]);

  if (loading) {
    return (
      <div className="w-12 h-12 rounded-full bg-gray-600 animate-pulse"></div>
    );
  }

  const generateFallbackSvg = (symbol: string) => {
    const svgString = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="48" height="48" rx="24" fill="#1a1a1a"/><text x="24" y="28" text-anchor="middle" fill="#666" font-size="12" font-family="Arial">${symbol || '?'}</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
  };

  const imgUrl = imageSrc || generateFallbackSvg(symbol);

  const handleMouseEnter = () => {
    if (imgRef.current && imageSrc) {
      const rect = imgRef.current.getBoundingClientRect();
      setPreviewPos({ x: rect.left, y: rect.top });
    }
  };

  return (
    <>
      <img
        ref={imgRef}
        className="w-12 h-12 rounded-full"
        src={imgUrl}
        alt={`${name} logo`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setPreviewPos(null)}
        onError={(e) => {
          // Try the next IPFS gateway before giving up on the logo.
          if (candidateIdx + 1 < candidates.length) { setCandidateIdx(candidateIdx + 1); return; }
          const target = e.target as HTMLImageElement;
          target.src = generateFallbackSvg(symbol);
        }}
      />
      {previewPos && imageSrc && ReactDOM.createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ left: previewPos.x, top: previewPos.y, transform: 'translate(-10px, -10px)' }}
        >
          <img
            className="w-[300px] h-[300px] rounded-xl border-2 border-gray-600 shadow-2xl object-cover bg-crypto-gray"
            src={imageSrc}
            alt={`${name} logo preview`}
          />
        </div>,
        document.body
      )}
    </>
  );
};

const StatsOverview: React.FC = () => {
  const [stats, setStats] = useState<{
    tokensAnalyzed: number;
    bundlesDetected: number;
    averageBundleRate: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold">Last 24 Hours</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-crypto-gray border border-gray-700 rounded-xl p-6 text-center animate-pulse">
              <div className="h-8 bg-gray-600 rounded mb-2"></div>
              <div className="h-4 bg-gray-600 rounded w-3/4 mx-auto"></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold">Last 24 Hours</h2>
        </div>
        <div className="text-center py-12">
          <div className="text-red-400 mb-4">
            <i className="fas fa-exclamation-triangle text-4xl"></i>
          </div>
          <p className="text-gray-400">Failed to load stats: {error}</p>
          <button
            onClick={fetchStats}
            className="mt-4 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-6 py-2 rounded-lg font-medium transition-all"
          >
            Try Again
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-2 mb-4">
      {/* Subtle gradient accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-3"></div>

      <div className="flex items-center justify-center mb-2">
        <h2 className="text-lg font-medium text-gray-400">Last 24 Hours</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-crypto-gray/50 border border-gray-700/50 rounded-xl py-3 px-4 text-center group hover:border-crypto-accent/30 transition-colors">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-crypto-accent/10 flex items-center justify-center">
              <i className="fas fa-search text-crypto-accent"></i>
            </div>
            <div className="text-3xl font-semibold text-crypto-accent">{stats?.tokensAnalyzed.toLocaleString() || '0'}</div>
          </div>
          <div className="text-sm text-gray-500">Tokens Analyzed</div>
        </div>
        <div className="bg-crypto-gray/50 border border-gray-700/50 rounded-xl py-3 px-4 text-center group hover:border-crypto-red/30 transition-colors">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-crypto-red/10 flex items-center justify-center">
              <i className="fas fa-exclamation-triangle text-crypto-red"></i>
            </div>
            <div className="text-3xl font-semibold text-crypto-red">{stats?.bundlesDetected.toLocaleString() || '0'}</div>
          </div>
          <div className="text-sm text-gray-500">Tokens Flagged</div>
        </div>
        <div className="bg-crypto-gray/50 border border-gray-700/50 rounded-xl py-3 px-4 text-center group hover:border-crypto-yellow/30 transition-colors">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-crypto-yellow/10 flex items-center justify-center">
              <i className="fas fa-chart-pie text-crypto-yellow"></i>
            </div>
            <div className="text-3xl font-semibold text-crypto-yellow">{stats?.averageBundleRate || '0.0'}%</div>
          </div>
          <div className="text-sm text-gray-500">Percent Flagged</div>
        </div>
      </div>
    </section>
  );
};

export default MainPage;
